/**
 * Feature Access Hook
 *
 * Provides permission-based access control for features like SongForge.
 * Uses the RBAC system to check if the current user has specific permissions.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { UserService } from '../api/services/userService';

// Feature permission mappings
export const FEATURE_PERMISSIONS = {
  SONGFORGE: 'songforge:access',
} as const;

export type FeatureKey = keyof typeof FEATURE_PERMISSIONS;

interface EffectivePermission {
  permission_id: string;
  permission_name: string;
  resource: string;
  action: string;
  granted: boolean;
  source_role_id: string;
  source_role_name: string;
}

export function useCurrentUserPermissions() {
  const { user, isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['current-user-permissions', user?.id],
    queryFn: async () => {
      if (!user?.id) return { permissions: [] };
      return UserService.getEffectivePermissions(user.id);
    },
    enabled: isAuthenticated && !!user?.id,
    staleTime: 60000, // 1 minute - permissions don't change often
    refetchOnWindowFocus: false,
  });
}

export function useHasPermission(resource: string, action: string): boolean {
  const { data, isLoading } = useCurrentUserPermissions();

  if (isLoading || !data?.permissions) return false;

  return data.permissions.some(
    (p: EffectivePermission) => p.resource === resource && p.action === action && p.granted
  );
}

export function useFeatureAccess(feature: FeatureKey): {
  hasAccess: boolean;
  isLoading: boolean;
} {
  const { isAdmin } = useAuth();
  const { data, isLoading } = useCurrentUserPermissions();

  // Admins always have access to all features
  if (isAdmin) {
    return { hasAccess: true, isLoading: false };
  }

  if (isLoading || !data?.permissions) {
    return { hasAccess: false, isLoading };
  }

  const permissionKey = FEATURE_PERMISSIONS[feature];
  const [resource, action] = permissionKey.split(':');

  const hasAccess = data.permissions.some(
    (p: EffectivePermission) => p.resource === resource && p.action === action && p.granted
  );

  return { hasAccess, isLoading: false };
}

// Convenience hook specifically for SongForge
export function useSongForgeAccess(): {
  hasAccess: boolean;
  isLoading: boolean;
} {
  return useFeatureAccess('SONGFORGE');
}
